import { t } from "@travily/i18n";
import type Badge from "@travily/types/models/BadgesRow";
import { cx } from "@ui/utils/cx";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { BadgeHoverCard } from "@/components/PassportBadges/BadgeHoverCard";
import { BadgeImage, passportSlot } from "@/components/PassportBadges/PassportSlot";
import { filterBadgesWithArtwork, resolveBadgeImage } from "@/images/badges";
import passportBook from "@/images/passport/passport-desktop.webp";
import passportPlaceholders from "@/images/passport/passport-desktop-placeholders.webp";
import passportNext from "@/images/passport/passport-next.png";
import { CurvedSectionTitle } from "./CurvedSectionTitle";
import { PassportCloseButton } from "./PassportCloseButton";
import { paginateBySection, type SectionPage } from "./paginate-by-section";

// Per-slot boxes as % of the placeholder *spread* (the dashed-arch layer overlaid
// on the book, NOT the whole book) measured from the alpha bbox of each arch in
// `passport-desktop-placeholders.webp` — NOT eyeballed. Slots and the dashed art
// therefore share one coordinate space and can't drift. Each badge sticker is a
// square sized to the slot width and centred in the arch. Order is reading order:
// left page top→bottom, then right page. Re-measure (analyse the placeholder
// image's dashed outlines) if the art is re-exported with shifted slots.
const SLOTS: Array<{ left: number; top: number; width: number }> = [
  { left: 0.0, top: 0.0, width: 18.59 },
  { left: 23.74, top: 0.47, width: 18.29 },
  { left: 0.0, top: 34.94, width: 18.29 },
  { left: 23.39, top: 35.8, width: 18.65 },
  { left: 0.06, top: 70.64, width: 18.65 },
  { left: 23.8, top: 71.12, width: 18.24 },
  { left: 57.96, top: 0.47, width: 18.29 },
  { left: 81.41, top: 0.0, width: 18.59 },
  { left: 57.96, top: 35.8, width: 18.65 },
  { left: 81.71, top: 34.94, width: 18.29 },
  { left: 57.96, top: 71.12, width: 18.24 },
  { left: 81.29, top: 70.64, width: 18.65 },
];

// SLOTS are reading order: indices 0–5 are the left page, 6–11 the right page.
const SLOTS_PER_PAGE = SLOTS.length / 2;

// The transparent placeholder spread sits inset over the book's two pages (its
// edges land on the page margins, clear of the cover). Tuned by compositing the
// placeholder over the clean background; the aspect ratio pins its height so only
// the offset matters. Slots above are % of THIS box.
const SPREAD = { left: "12.63%", top: "16.7%", width: "74.73%" } as const;

// A light, repeating tilt so the stickers feel hand-placed ("higgledy-piggledy")
// rather than gridded. Keyed by slot index so it's stable across renders.
const TILTS = [-1.2, 0.8, 1.1, -0.7, 0.6, -1, 1.2, -0.6, 0.9, -1.1, 0.7, -0.9];

const slotStyles = passportSlot({ size: "fluid", rotation: "none" });

// Nav buttons sit just inside the book's left/right edges (over the cover
// margins, clear of the slots) so they're never clipped by the modal overflow.
// The art (`passport-next.png`) is a rendered dark disc + arrow + shadow, so the
// button itself is just a focusable, disable-able wrapper.
const navButton =
  "absolute top-1/2 z-20 size-12 -translate-y-1/2 rounded-full outline-none transition disabled:cursor-not-allowed disabled:opacity-30 focus-visible:ring-2 focus-visible:ring-white enabled:hover:scale-105";

export function PassportBook({ badges, onClose }: { badges: Badge[]; onClose: () => void }) {
  const reducedMotion = useReducedMotion() ?? false;

  // Hold the badges/titles hidden until the book backdrop has painted. The book is
  // a single heavy illustration; the SVG titles and small badge stickers would
  // otherwise flash onto a blank modal before it arrives. We gate only the visual
  // reveal (not the DOM) so a11y, arrow-key paging, and the live region are
  // unaffected. `onError` also releases the gate so a failed image never hides the
  // contents forever.
  const bookRef = useRef<HTMLImageElement>(null);
  const [bookReady, setBookReady] = useState(false);
  useEffect(() => {
    // A cached image can be `complete` before React attaches `onLoad`.
    if (bookRef.current?.complete) setBookReady(true);
  }, []);

  // Drop badges whose artwork isn't in the client bundle before paginating (mirrors
  // PassportNotepad and the badge strip), so an unresolvable badge never consumes a
  // slot as a phantom empty arch or shifts a section run.
  const resolved = filterBadgesWithArtwork(badges);

  // Section-first pages (≤6 badges each, a new section always starting a fresh page),
  // paired into two-page spreads purely for display — the section boundaries already
  // fall on page boundaries, so pairing never merges sections across the gutter.
  const pages = paginateBySection(resolved);
  const spreads: Array<[SectionPage | undefined, SectionPage | undefined]> = [];
  for (let i = 0; i < pages.length; i += 2) spreads.push([pages[i], pages[i + 1]]);
  const pageCount = Math.max(1, spreads.length);

  const [spread, setSpread] = useState(0);
  const goPrev = useCallback(() => setSpread((s) => Math.max(0, s - 1)), []);
  const goNext = useCallback(() => setSpread((s) => Math.min(pageCount - 1, s + 1)), [pageCount]);

  // Arrow-key paging via a listener on the container (not a JSX handler on the
  // presentational book) so it stays keyboard-accessible without an interactive
  // role on a decorative element.
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = rootRef.current;
    if (!node) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") goPrev();
      if (event.key === "ArrowRight") goNext();
    };
    node.addEventListener("keydown", handler);
    return () => node.removeEventListener("keydown", handler);
  }, [goPrev, goNext]);

  // Clamp against pageCount in case the badge set shrank since `spread` was set
  // (a stale index would otherwise land on an undefined spread = blank pages).
  const [leftPage, rightPage] = spreads[Math.min(spread, pageCount - 1)] ?? [undefined, undefined];

  return (
    // Capped well inside the modal so the book + its baked shadow + the inset
    // controls never reach the clipping overlay/dialog edges.
    <div ref={rootRef} className="relative mx-auto aspect-[2260/1585] w-[min(820px,86vw,calc((100dvh-8rem)*1.426))]">
      <img
        ref={bookRef}
        src={passportBook}
        alt=""
        draggable={false}
        className="absolute inset-0 size-full select-none"
        onLoad={() => setBookReady(true)}
        onError={() => setBookReady(true)}
      />

      <PassportCloseButton onClose={onClose} className="top-[3.5%] right-[2%] size-10" />

      {/* Reveal the titles + badges only once the book backdrop has painted. While
          hidden it's also non-interactive, so a badge hovercard can't be popped over
          a still-blank book. */}
      <div
        className={cx("absolute inset-0", !bookReady && "pointer-events-none")}
        style={{ opacity: bookReady ? 1 : 0, transition: reducedMotion ? undefined : "opacity 300ms ease-out" }}
      >
        <AnimatePresence mode="wait">
          {/* One animated layer per spread so the section titles cross-fade with their
              pages on a page turn. Titles live in the book's top margin; the badge
              SPREAD box sits below them in its measured inset. */}
          <motion.div
            key={spread}
            className="absolute inset-0"
            initial={reducedMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={reducedMotion ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.18 }}
          >
            {/* A section title shows once per spread, anchored to the leftmost page the
              section occupies. The left page always carries its title; the right page
              only when it heads a different section (a section spilling left→right is
              already titled on the left). `continued` is true whenever the page isn't
              the section's first page in the run, regardless of where it sits. */}
            {leftPage && (
              <CurvedSectionTitle
                section={leftPage.section}
                variant="left"
                continued={!leftPage.showTitle}
                className="absolute inset-0 size-full"
              />
            )}
            {rightPage && rightPage.section !== leftPage?.section && (
              <CurvedSectionTitle
                section={rightPage.section}
                variant="right"
                continued={!rightPage.showTitle}
                className="absolute inset-0 size-full"
              />
            )}

            {/* The dashed-arch placeholder layer + the badges share this inset box, so a
              badge always lands on its slot regardless of book size. The dashed arch is
              drawn ONLY for empty slots (it's the "fill me" affordance) — a filled badge
              replaces it rather than sitting on top of it. */}
            <div
              className="absolute"
              style={{ left: SPREAD.left, top: SPREAD.top, width: SPREAD.width, aspectRatio: "1689 / 1056" }}
            >
              {SLOTS.map((slot, index) => {
                const page = index < SLOTS_PER_PAGE ? leftPage : rightPage;
                const badge = page?.badges[index % SLOTS_PER_PAGE];
                const url = badge ? resolveBadgeImage(badge.image_key) : null;
                if (badge && url) {
                  return (
                    <div
                      key={badge.id}
                      className="absolute"
                      style={{
                        left: `${slot.left}%`,
                        top: `${slot.top}%`,
                        width: `${slot.width}%`,
                        rotate: `${TILTS[index] ?? 0}deg`,
                      }}
                    >
                      <BadgeHoverCard badge={badge} triggerClassName={cx(slotStyles.slot(), "block")}>
                        <BadgeImage url={url} alt={badge.label} imageKey={badge.image_key} />
                      </BadgeHoverCard>
                    </div>
                  );
                }
                // Empty slot: show just this slot's dashed arch, clipped out of the
                // shared placeholder layer (its arches are positioned for the spread).
                const heightPct = slot.width * (1689 / 1056);
                return (
                  <img
                    key={`empty-${slot.left}-${slot.top}`}
                    src={passportPlaceholders}
                    alt=""
                    draggable={false}
                    className="absolute inset-0 size-full select-none"
                    style={{
                      clipPath: `inset(${slot.top}% ${100 - (slot.left + slot.width)}% ${100 - (slot.top + heightPct)}% ${slot.left}%)`,
                    }}
                  />
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {pageCount > 1 && (
        <>
          <button
            type="button"
            aria-label={t("passport.badges.modal.previous_page")}
            className={cx(navButton, "left-[1.5%]")}
            disabled={spread === 0}
            onClick={goPrev}
          >
            <img src={passportNext} alt="" draggable={false} className="size-full -scale-x-100 select-none" />
          </button>
          <button
            type="button"
            aria-label={t("passport.badges.modal.next_page")}
            className={cx(navButton, "right-[1.5%]")}
            disabled={spread >= pageCount - 1}
            onClick={goNext}
          >
            <img src={passportNext} alt="" draggable={false} className="size-full select-none" />
          </button>
        </>
      )}

      <output className="sr-only" aria-live="polite">
        {t("passport.badges.modal.page_of", { page: spread + 1, pages: pageCount })}
      </output>
    </div>
  );
}
